import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { notifyHost } from '../../../lib/notificationDispatch';
import Icon from '../../../components/AppIcon';
import ToastNotification from './ToastNotification';

const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  denied: 'bg-red-100 text-red-800 border-red-200',
};

const DeleteRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('requests'); // 'requests' | 'deleted'

  // Modal state
  const [confirmModal, setConfirmModal] = useState(null); // { type: 'delete'|'deny', request }
  const [adminNote, setAdminNote] = useState('');
  const [modalError, setModalError] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        ?.from('event_delete_requests')
        ?.select(`
          id, host_reason, status, admin_note, reviewed_at, created_at,
          event_id,
          host_id,
          events (
            id, title, image_url, start_date, category
          ),
          event_host_profiles (
            id, full_name, organization_name, email, avatar_url
          )
        `)
        ?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setRequests(data || []);
    } catch (err) {
      console.error('fetchDeleteRequests error:', err);
      setError(err?.message || 'Failed to load delete requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3500);
  };

  const openDeleteModal = (request) => {
    setConfirmModal({ type: 'delete', request });
    setAdminNote('');
    setModalError(null);
  };

  const openDenyModal = (request) => {
    setConfirmModal({ type: 'deny', request });
    setAdminNote('');
    setModalError(null);
  };

  const closeModal = () => {
    setConfirmModal(null);
    setAdminNote('');
    setModalError(null);
  };

  const handleDeleteEvent = async () => {
    if (!confirmModal?.request) return;
    setActionLoading(true);
    setModalError(null);
    try {
      const req = confirmModal?.request;

      // Mark request as approved first
      const { error: reqError } = await supabase
        ?.from('event_delete_requests')
        ?.update({
          status: 'approved',
          admin_note: adminNote?.trim() || null,
          reviewed_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', req?.id);
      if (reqError) throw reqError;

      const eventTitle = req?.events?.title || 'Your event';
      const host = req?.event_host_profiles;
      await notifyHost({
        userId: req?.host_id,
        eventId: null, // event is about to be deleted
        notificationType: 'event_deleted',
        title: '🗑️ Event Deleted',
        body: `"${eventTitle}" has been deleted as requested.`,
        emailTemplate: 'admin_notification',
        emailTo: host?.email,
        emailSubject: 'Your KidQuest Event Was Deleted',
        emailData: {
          user_name: host?.full_name || 'there',
          heading: 'Event Deleted',
          emoji: '🗑️',
          intro: 'As requested, the KidQuest team has deleted your event.',
          detail_label: 'Event',
          detail_value: eventTitle,
          tone: 'negative',
        },
      });

      // Permanently delete the event
      const { error: deleteError } = await supabase
        ?.from('events')
        ?.delete()
        ?.eq('id', req?.event_id);
      if (deleteError) throw deleteError;

      showSuccess(`Event "${req?.events?.title || 'Event'}" has been permanently deleted.`);
      closeModal();
      fetchRequests();
    } catch (err) {
      setModalError(err?.message || 'Failed to delete event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDenyRequest = async () => {
    if (!confirmModal?.request) return;
    setActionLoading(true);
    setModalError(null);
    try {
      const req = confirmModal?.request;

      const { error: reqError } = await supabase
        ?.from('event_delete_requests')
        ?.update({
          status: 'denied',
          admin_note: adminNote?.trim() || null,
          reviewed_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', req?.id);
      if (reqError) throw reqError;

      const eventTitle = req?.events?.title || 'Your event';
      const host = req?.event_host_profiles;
      const note = adminNote?.trim();
      await notifyHost({
        userId: req?.host_id,
        eventId: req?.event_id,
        notificationType: 'deletion_denied',
        title: '🚫 Deletion Request Denied',
        body: note
          ? `"${eventTitle}" deletion request was denied. Reason: ${note}`
          : `"${eventTitle}" deletion request was denied by admin.`,
        emailTemplate: 'admin_notification',
        emailTo: host?.email,
        emailSubject: 'Update on Your KidQuest Deletion Request',
        emailData: {
          user_name: host?.full_name || 'there',
          heading: 'Deletion Request Denied',
          emoji: '🚫',
          intro: 'The KidQuest team reviewed your request to delete this event and denied it — the event remains live.',
          detail_label: 'Event',
          detail_value: eventTitle,
          detail_label_2: note ? 'Reason' : '',
          detail_value_2: note || '',
          tone: 'negative',
        },
      });

      showSuccess('Delete request denied. The event remains live.');
      closeModal();
      fetchRequests();
    } catch (err) {
      setModalError(err?.message || 'Failed to deny request');
    } finally {
      setActionLoading(false);
    }
  };

  // Filter requests based on active filter
  const filteredRequests = requests?.filter((req) => {
    if (activeFilter === 'requests') return req?.status === 'pending';
    if (activeFilter === 'deleted') return req?.status === 'approved';
    return true;
  });

  const pendingCount = requests?.filter((r) => r?.status === 'pending')?.length;
  const deletedCount = requests?.filter((r) => r?.status === 'approved')?.length;

  return (
    <div>
      {/* Popup Toast Notifications */}
      <ToastNotification
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />

      {/* Filter Toggle */}
      <div className="flex items-center gap-2 mb-5">
        <button
          onClick={() => setActiveFilter('requests')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
            activeFilter === 'requests' ?'bg-primary text-primary-foreground border-primary' :'bg-card text-muted-foreground border-border hover:bg-muted'
          }`}
        >
          <Icon name="Clock" size={14} />
          Requests
          {pendingCount > 0 && (
            <span
              className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${
                activeFilter === 'requests' ?'bg-white/20 text-white' :'bg-yellow-100 text-yellow-800'
              }`}
            >
              {pendingCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveFilter('deleted')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
            activeFilter === 'deleted'
              ? 'bg-primary text-primary-foreground border-primary' :'bg-card text-muted-foreground border-border hover:bg-muted'
          }`}
        >
          <Icon name="Trash2" size={14} />
          Deleted
          {deletedCount > 0 && (
            <span
              className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${
                activeFilter === 'deleted'
                  ? 'bg-white/20 text-white' :'bg-red-100 text-red-800'
              }`}
            >
              {deletedCount}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading delete requests...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Icon name="AlertCircle" size={32} className="text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <button
            onClick={fetchRequests}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : filteredRequests?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Icon name={activeFilter === 'deleted' ? 'Trash2' : 'Clock'} size={32} className="text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {activeFilter === 'deleted' ? 'No deleted events found' : 'No pending delete requests'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests?.map((req) => {
            const event = req?.events;
            const host = req?.event_host_profiles;
            const isPending = req?.status === 'pending';
            const isDeleted = req?.status === 'approved';

            return (
              <div
                key={req?.id}
                className={`bg-card border rounded-xl p-5 space-y-4 ${
                  isDeleted ? 'border-red-200 opacity-80' : 'border-border'
                }`}
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    {/* Event thumbnail */}
                    <div className="relative flex-shrink-0">
                      {event?.image_url ? (
                        <img
                          src={event?.image_url}
                          alt={event?.title || 'Event thumbnail'}
                          className={`w-14 h-14 rounded-lg object-cover ${isDeleted ? 'grayscale' : ''}`}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center">
                          <Icon name="Calendar" size={20} className="text-muted-foreground" />
                        </div>
                      )}
                      {isDeleted && (
                        <div className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                          DEL
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-semibold text-sm truncate ${isDeleted ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                          {event?.title || '(Event deleted)'}
                        </h3>
                        {isDeleted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-red-100 text-red-700 border-red-200">
                            <Icon name="Trash2" size={10} />
                            Deleted
                          </span>
                        ) : (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_BADGE?.[req?.status]}`}
                          >
                            {req?.status === 'pending' ? 'Pending'
                              : req?.status === 'approved' ? 'Approved' : 'Denied'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Requested by{' '}
                        <span className="font-medium text-foreground">
                          {host?.organization_name || host?.full_name || '—'}
                        </span>
                        {host?.email && (
                          <span className="ml-1 text-muted-foreground">({host?.email})</span>
                        )}
                      </p>
                      {event?.start_date && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Event date:{' '}
                          {new Date(event?.start_date)?.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                    {req?.created_at
                      ? new Date(req?.created_at)?.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '—'}
                  </p>
                </div>

                {/* Host's reason */}
                <div className="bg-muted/40 border border-border rounded-lg p-4 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Host's Reason for Deletion
                  </p>
                  <p className="text-sm text-foreground">{req?.host_reason}</p>
                </div>

                {/* Admin note (if resolved) */}
                {req?.admin_note && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
                    <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Admin Note</p>
                    <p className="text-sm text-blue-800">{req?.admin_note}</p>
                  </div>
                )}

                {/* Reviewed at */}
                {req?.reviewed_at && (
                  <p className="text-xs text-muted-foreground">
                    {isDeleted ? 'Deleted on' : 'Reviewed on'}{' '}
                    {new Date(req?.reviewed_at)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                )}

                {/* Actions — only for pending */}
                {isPending && (
                  <div className="flex items-center gap-3 pt-1">
                    <button
                      onClick={() => openDeleteModal(req)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Icon name="Trash2" size={14} />
                      Delete Event
                    </button>
                    <button
                      onClick={() => openDenyModal(req)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                    >
                      <Icon name="X" size={14} />
                      Deny
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                {confirmModal?.type === 'delete' ? 'Confirm Event Deletion' : 'Deny Delete Request'}
              </h3>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {confirmModal?.type === 'delete' ? (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <Icon name="AlertTriangle" size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">
                    This will <strong>permanently delete</strong> the event{' '}
                    <span className="font-semibold">"{confirmModal?.request?.events?.title || 'this event'}"</span>{' '}
                    and remove it from all listings. This action cannot be undone.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  The delete request will be closed and the event{' '}
                  <span className="font-medium text-foreground">
                    "{confirmModal?.request?.events?.title || 'this event'}"
                  </span>{' '}
                  will remain live.
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Admin Note{' '}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e?.target?.value)}
                  placeholder={
                    confirmModal?.type === 'delete'
                      ? 'Add a note for the host (optional)...' :'Explain why the request is being denied (optional)...'
                  }
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              </div>

              {modalError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  <Icon name="AlertCircle" size={14} />
                  {modalError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-xl hover:bg-muted transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal?.type === 'delete' ? handleDeleteEvent : handleDenyRequest}
                  disabled={actionLoading}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 ${
                    confirmModal?.type === 'delete'
                      ? 'bg-red-600 text-white hover:bg-red-700' :'bg-foreground text-background hover:bg-foreground/90'
                  }`}
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : confirmModal?.type === 'delete' ? (
                    <>
                      <Icon name="Trash2" size={14} />
                      Delete Event
                    </>
                  ) : (
                    <>
                      <Icon name="X" size={14} />
                      Deny Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteRequestsTab;
